import {
  ColumnDefinition,
  OuiBoxComponent,
  OuiButtonComponent,
  OuiCenterComponent,
  OuiDataTableComponent,
  OuiDialogComponent,
  OuiPaginatorComponent,
  OuiServerSideDatasource as OuiDatasource,
  OuiStackComponent,
  OuiTextComponent,
  NotificationService,
} from '@aiz-gl/omni-ui';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, effect, inject, Input, signal, TemplateRef, viewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { catchError, firstValueFrom, forkJoin, map as rxMap, of, Subscription } from 'rxjs';
import { ImageAttachment, ImageAttachmentSliderComponent } from '../../components/image-attachment-slider/image-attachment-slider.component';
import { ConfigMappingResponse, RmaDecisionRequest, RmaDetailItemResponse, RmaDetailResponse, RmaResubmitItemRequest, RmaResubmitRequest, WarehouseChannel } from '../../models/rma.models';
import { RmaService } from '../../services/rma/rma.service';
import { BasePageComponent } from '../base/base.component';

type DeviceDetailRow = {
  itemId: number;
  device: string;
  deviceMeta: string;
  specsCapacity: string;
  specsCondition: string;
  unitPrice: string;
  creditAmount: string;
  compliance: {
    itemId: number;
    status: string;
  };
  action: null;
};

type TimelineItem = {
  title: string;
  subtitle: string;
  variant: string;
};

type ResubmitReasonOption = {
  label: string;
  value: string;
};

@Component({
  selector: 'app-rma-details',
  standalone: true,
  imports: [
    OuiBoxComponent,
    OuiButtonComponent,
    OuiCenterComponent,
    OuiDataTableComponent,
    OuiDialogComponent,
    OuiPaginatorComponent,
    OuiStackComponent,
    OuiTextComponent,
    ImageAttachmentSliderComponent,
    RouterLink,
  ],
  templateUrl: './rma-details.component.html',
  styleUrl: './rma-details.component.css',
})
export class RmaDetailsPage extends BasePageComponent {
  private readonly rmaService = inject(RmaService);
  private readonly notifications = inject(NotificationService);
  readonly deviceCell = viewChild<TemplateRef<never>>('deviceCell');
  readonly specsCell = viewChild<TemplateRef<never>>('specsCell');
  readonly complianceCell = viewChild<TemplateRef<never>>('complianceCell');
  readonly resubmitCreditAmountCell = viewChild<TemplateRef<never>>('resubmitCreditAmountCell');
  readonly resubmitRemoveCell = viewChild<TemplateRef<never>>('resubmitRemoveCell');

  rmaDetail = signal<RmaDetailResponse | null>(null);
  activityItems = signal<TimelineItem[]>([]);
  attachments = signal<ImageAttachment[]>([]);
  attachmentsLoading = signal(false);
  attachmentImagesLoading = signal(false);
  attachmentThumbnailUrls = signal<Record<string, string>>({});
  private attachmentBatchSubscription: Subscription | null = null;
  private attachmentViewerOpen = false;
  private initialAttachmentBatchStarted = false;
  private readonly attemptedAttachmentImageIds = new Set<string>();
  approverWarehouseChannels = signal<WarehouseChannel[]>([]);
  creatorWarehouseChannels = signal<WarehouseChannel[]>([]);
  decisionComments = signal('');
  rejectionReason = signal('');
  rejectionReasonTouched = signal(false);
  activeDecisionDialog = signal<'approved' | 'rejected' | null>(null);
  complianceStatusByItemId = signal<Record<number, string>>({});
  submittingDecision = signal(false);
  resubmitStarted = signal(false);
  removedResubmitItemIds = signal<Set<number>>(new Set());
  resubmitCreditAmountByItemId = signal<Record<number, string>>({});
  resubmitCreditValidationByItemId = signal<Record<number, string>>({});
  showResubmitDialog = signal(false);
  resubmitReasonOptions = signal<ResubmitReasonOption[]>([]);
  resubmitReasonCode = signal('');
  resubmitReasonTouched = signal(false);
  resubmitNotes = signal('');
  resubmitInlineError = signal('');
  submittingResubmit = signal(false);
  readonly decisionCommentsMaxLength = 4000;
  readonly rejectionReasonMaxLength = 4000;
  readonly resubmitNotesMaxLength = 512;
  private readonly fallbackResubmitReasonOptions: ResubmitReasonOption[] = [
    { label: 'Rejected By Mistake', value: 'REJECTED_BY_MISTAKE' },
    { label: 'Incorrect Details Updated (IMEI and Credit Value)', value: 'INCORRECT_DETAILS_UPDATED_IMEI_AND_CREDIT_VALUE' },
    { label: 'Additional Info Updated ( IMEI)', value: 'ADDITIONAL_INFO_UPDATED_IMEI' },
    { label: 'Device Updated', value: 'DEVICE_UPDATED' },
    { label: 'Other', value: 'OTHER' },
  ];
  readonly complianceOptions = ['Pending', 'Compliant', 'Non Compliant'];
  deviceDetailColumns!: ColumnDefinition<DeviceDetailRow>[];

  constructor() {
    super();

    effect(() => {
      const detail = this.rmaDetail();
      const showResubmitRemoveAction = this.shouldShowResubmitRemoveAction();
      const canEditResubmitCreditAmount = this.canEditResubmitCreditAmount();
      const columns: ColumnDefinition<DeviceDetailRow>[] = [
        {
          columnDef: 'device',
          header: 'Device',
          template: this.deviceCell(),
          textAlign: 'left',
        },
        {
          columnDef: 'specsCapacity',
          header: 'Specs',
          template: this.specsCell(),
          textAlign: 'left',
        },
        {
          columnDef: 'unitPrice',
          header: 'Unit Price',
        },
      ];

      if (canEditResubmitCreditAmount) {
        columns.push({
          columnDef: 'itemId',
          header: 'Credit Amount',
          template: this.resubmitCreditAmountCell(),
          textAlign: 'right',
        });
      } else if (this.isCreditOnlyRma(detail?.rma_type)) {
        columns.push({
          columnDef: 'creditAmount',
          header: 'Credit Amount',
        });
      }

      columns.push(
        {
          columnDef: 'itemId',
          header: 'Compliance',
          template: this.complianceCell(),
          textAlign: 'center',
        },
      );

      if (showResubmitRemoveAction) {
        columns.push({
          columnDef: 'action',
          header: 'Action',
          template: this.resubmitRemoveCell(),
          textAlign: 'center',
        });
      }

      this.deviceDetailColumns = columns;
    });
  }

  readonly timelineItems = computed(() => {
    const activityItems = this.activityItems();
    const detail = this.rmaDetail();

    if (!detail) {
      return [];
    }

    return activityItems;
  });

  readonly deviceRows = computed(() => {
    const detail = this.rmaDetail();
    if (!detail) {
      return [];
    }

    return detail.rma_items.map((item) => ({
      itemId: this.getRmaItemId(item),
      deviceName: this.getItemDeviceName(item),
      serialNumber: item.serial_number || '-',
      capacity: this.getItemCapacity(item),
      condition: this.getItemCondition(item),
      unitPrice: this.formatAmount(this.getItemUnitPrice(item)),
      creditAmount: this.formatAmount(item.credit_amount),
      compliance: item.compliance_status || '-',
    }));
  });

  readonly deviceDetailsDatasource = new OuiDatasource<DeviceDetailRow>(
    async (request) => {
      const rows = this.deviceRows().map((row) => ({
        itemId: row.itemId,
        device: row.deviceName,
        deviceMeta: `IMEI / Serial: ${row.serialNumber}`,
        specsCapacity: row.capacity,
        specsCondition: row.condition,
        unitPrice: row.unitPrice,
        creditAmount: row.creditAmount,
        compliance: {
          itemId: row.itemId,
          status: row.compliance,
        },
        action: null,
      }));

      const pageSize = request.pageSize || 10;
      const pageNumber = request.pageNumber || 1;
      const start = (pageNumber - 1) * pageSize;
      const end = start + pageSize;

      return {
        data: rows.slice(start, end),
        totalRecords: rows.length,
        pageNumber,
        pageSize,
      };
    },
    10,
  );

  readonly consolidatedUnitPrice = computed(() => {
    const detail = this.rmaDetail();
    if (!detail || !detail.rma_items.length) {
      return null;
    }

    const prices = detail.rma_items
      .map((item) => this.getItemUnitPrice(item))
      .filter((price): price is number => typeof price === 'number');

    if (!prices.length) {
      return null;
    }

    return prices.reduce((sum, price) => sum + price, 0);
  });

  readonly totalUnits = computed(() => {
    const detail = this.rmaDetail();
    return detail?.rma_items?.length ?? 0;
  });

  @Input()
  set rmaId(rmaId: number) {
    if (!rmaId || Number.isNaN(rmaId)) {
      this.rmaDetail.set(null);
      this.activityItems.set([]);
      this.approverWarehouseChannels.set([]);
      this.decisionComments.set('');
      this.rejectionReason.set('');
      this.rejectionReasonTouched.set(false);
      this.activeDecisionDialog.set(null);
      this.complianceStatusByItemId.set({});
      this.resetResubmitState();
      this.deviceDetailsDatasource.setFilter({ refreshAt: Date.now() });
      return;
    }

    void this.loadRmaDetailAndActivity(rmaId);
  }

  getStatusBadgeClass(status: string | undefined): string {
    const normalized = (status || '').trim().toLowerCase();
    if (normalized.includes('approved')) return 'chip chip-approved';
    if (normalized.includes('reject')) return 'chip chip-rejected';
    if (normalized.includes('review') || normalized.includes('pending')) return 'chip chip-review';
    return 'chip';
  }

  getComplianceBadgeClass(status: string | undefined): string {
    const normalized = (status || '').trim().toLowerCase();
    if (normalized.includes('approved')) return 'chip chip-compliance chip-approved';
    if (normalized.includes('complete')) return 'chip chip-compliance chip-approved';
    if (normalized.includes('reject')) return 'chip chip-compliance chip-rejected';
    if (normalized.includes('cancel')) return 'chip chip-compliance chip-rejected';
    if (normalized.includes('review') || normalized.includes('pending')) return 'chip chip-compliance chip-review';
    if (normalized.includes('non')) return 'chip chip-compliance chip-rejected';
    if (normalized.includes('compliant')) return 'chip chip-compliance chip-approved';
    return 'chip chip-compliance';
  }

  shouldShowApprovalActions(detail: RmaDetailResponse): boolean {
    const normalizedStatus = (detail.status || '').replace(/[^a-zA-Z]/g, '').toLowerCase();
    return normalizedStatus === 'underreview' && this.isApproverForRmaWarehouse(detail);
  }

  shouldShowResubmitAction(detail: RmaDetailResponse): boolean {
    return (
      this.isRejectedRma(detail.status) &&
      this.getResubmissionCount(detail) === 0 &&
      this.canCreateRmaForWarehouseAndChannel(detail) &&
      !this.resubmitStarted()
    );
  }

  canRemoveResubmitItems(): boolean {
    const detail = this.rmaDetail();
    return (
      !!detail &&
      this.resubmitStarted() &&
      detail.rma_items.length > 1 &&
      (this.isRegularRma(detail.rma_type) || this.isCreditOnlyRma(detail.rma_type))
    );
  }

  shouldShowResubmitRemoveAction(): boolean {
    const detail = this.rmaDetail();
    return (
      !!detail &&
      this.resubmitStarted() &&
      (this.isRegularRma(detail.rma_type) || this.isCreditOnlyRma(detail.rma_type))
    );
  }

  canEditResubmitCreditAmount(): boolean {
    const detail = this.rmaDetail();
    return !!detail && this.resubmitStarted() && this.isCreditOnlyRma(detail.rma_type);
  }

  canEditComplianceStatus(): boolean {
    const detail = this.rmaDetail();
    return detail ? this.shouldShowApprovalActions(detail) : false;
  }

  getComplianceDropdownValue(status: string): string {
    const normalizedStatus = status.replace(/[^a-zA-Z]/g, '').toLowerCase();
    const option = this.complianceOptions.find(
      (value) => value.replace(/[^a-zA-Z]/g, '').toLowerCase() === normalizedStatus,
    );

    return option ?? 'Pending';
  }

  getItemComplianceStatus(itemId: number | string): string {
    const normalizedItemId = Number(itemId);
    const selectedStatus = this.complianceStatusByItemId()[normalizedItemId];
    if (selectedStatus) {
      return selectedStatus;
    }

    const detail = this.rmaDetail();
    const item = detail?.rma_items.find((current) => this.getRmaItemId(current) === normalizedItemId);
    return item?.compliance_status || '-';
  }

  getComplianceCellItemId(compliance: DeviceDetailRow['compliance']): number {
    return compliance.itemId;
  }

  getComplianceCellStatus(compliance: DeviceDetailRow['compliance']): string {
    return this.getItemComplianceStatus(compliance.itemId) || compliance.status || '-';
  }

  onComplianceStatusChange(itemId: number | string, status: string) {
    const normalizedItemId = Number(itemId);
    if (!Number.isFinite(normalizedItemId) || !this.canEditComplianceStatus()) {
      return;
    }

    this.complianceStatusByItemId.update((current) => ({
      ...current,
      [normalizedItemId]: status,
    }));
  }

  onDecisionCommentsChange(value: string) {
    this.decisionComments.set((value || '').slice(0, this.decisionCommentsMaxLength));
  }

  getResubmitCreditAmount(itemId: number | string): string {
    const normalizedItemId = Number(itemId);
    return this.resubmitCreditAmountByItemId()[normalizedItemId] || '';
  }

  onResubmitCreditAmountChange(itemId: number | string, value: string) {
    const normalizedItemId = Number(itemId);
    if (!Number.isFinite(normalizedItemId) || !this.canEditResubmitCreditAmount()) {
      return;
    }

    this.resubmitCreditAmountByItemId.update((current) => ({
      ...current,
      [normalizedItemId]: value,
    }));
    this.updateResubmitCreditValidation(normalizedItemId, value);
  }

  getResubmitCreditValidationMessage(itemId: number | string): string {
    return this.resubmitCreditValidationByItemId()[Number(itemId)] || '';
  }

  isResubmitItemRemoved(itemId: number | string): boolean {
    const normalizedItemId = Number(itemId);
    return Number.isFinite(normalizedItemId) && this.removedResubmitItemIds().has(normalizedItemId);
  }

  toggleResubmitItemRemoved(itemId: number | string) {
    const normalizedItemId = Number(itemId);
    if (!Number.isFinite(normalizedItemId)) {
      return;
    }

    if (!this.canRemoveResubmitItems()) {
      this.notifications.showAlert({
        variant: 'warn',
        symbolName: 'report',
        message: 'At least one device per Return is mandatory.',
      });
      return;
    }

    this.removedResubmitItemIds.update((current) => {
      const next = new Set(current);
      if (next.has(normalizedItemId)) {
        next.delete(normalizedItemId);
        this.updateResubmitCreditValidation(
          normalizedItemId,
          this.getResubmitCreditAmount(normalizedItemId),
        );
      } else {
        next.add(normalizedItemId);
        this.resubmitCreditValidationByItemId.update((errors) => {
          const nextErrors = { ...errors };
          delete nextErrors[normalizedItemId];
          return nextErrors;
        });
      }
      return next;
    });
  }

  async startResubmitForApproval() {
    const detail = this.rmaDetail();
    if (!detail || !this.shouldShowResubmitAction(detail)) {
      return;
    }

    this.resubmitStarted.set(true);
    this.resubmitInlineError.set('');
    this.removedResubmitItemIds.set(new Set());
    this.resubmitCreditValidationByItemId.set({});
    this.resubmitCreditAmountByItemId.set(
      Object.fromEntries(
        detail.rma_items.map((item) => [
          this.getRmaItemId(item),
          this.getInitialResubmitCreditAmount(item),
        ]),
      ),
    );
    this.deviceDetailsDatasource.setFilter({ refreshAt: Date.now() });
    await this.loadResubmitReasonOptions();
  }

  openResubmitReviewDialog() {
    if (!this.resubmitStarted()) {
      return;
    }

    const detail = this.rmaDetail();
    if (detail && !this.validateResubmitCreditAmounts(detail)) {
      this.resubmitInlineError.set('Credit value cannot exceed the device unit price.');
      return;
    }

    this.resubmitReasonCode.set('');
    this.resubmitReasonTouched.set(false);
    this.resubmitNotes.set('');
    this.resubmitInlineError.set('');
    this.showResubmitDialog.set(true);
    void this.loadResubmitReasonOptions();
  }

  closeResubmitReviewDialog() {
    if (this.submittingResubmit()) {
      return;
    }

    this.showResubmitDialog.set(false);
    this.resubmitReasonCode.set('');
    this.resubmitReasonTouched.set(false);
    this.resubmitNotes.set('');
  }

  onResubmitReasonChange(value: string) {
    this.resubmitReasonCode.set(value || '');
    this.resubmitReasonTouched.set(true);
    this.resubmitInlineError.set('');
  }

  onResubmitNotesChange(value: string) {
    this.resubmitNotes.set((value || '').slice(0, this.resubmitNotesMaxLength));
  }

  getResubmitReasonError(): string {
    return this.resubmitReasonTouched() && !this.resubmitReasonCode()
      ? 'Reason is required.'
      : '';
  }

  async submitResubmitForApproval() {
    const detail = this.rmaDetail();
    if (!detail || this.submittingResubmit()) {
      return;
    }

    if (!this.resubmitReasonCode()) {
      this.resubmitReasonTouched.set(true);
      return;
    }

    if (!this.validateResubmitCreditAmounts(detail)) {
      this.resubmitInlineError.set('Credit value cannot exceed the device unit price.');
      return;
    }

    const remainingItemCount = detail.rma_items.filter(
      (item) => !this.removedResubmitItemIds().has(this.getRmaItemId(item)),
    ).length;
    if (remainingItemCount === 0) {
      this.resubmitInlineError.set('At least one device is required for resubmission.');
      return;
    }

    const payload: RmaResubmitRequest = {
      reason_code: this.resubmitReasonCode(),
      notes: this.resubmitNotes().trim(),
      items: this.buildResubmitItems(detail),
    };

    this.submittingResubmit.set(true);
    this.resubmitInlineError.set('');

    try {
      await this.rmaService.resubmitRma(detail.rma_id, payload);
      this.notifications.showAlert({
        variant: 'success',
        symbolName: 'check_circle',
        message: 'RMA successfully resubmitted for approval.',
      });
      this.showResubmitDialog.set(false);
      this.resetResubmitState();
      this.rmaDetail.set({
        ...detail,
        status: 'UnderReview',
        resubmission_count: 1,
        resubmission_reason: this.getSelectedResubmitReasonLabel(),
        resubmission_notes: payload.notes,
      });
      await this.loadRmaDetailAndActivity(detail.rma_id);
    } catch (error) {
      this.handleResubmitError(error);
    } finally {
      this.submittingResubmit.set(false);
    }
  }

  private resetResubmitState() {
    this.resubmitStarted.set(false);
    this.removedResubmitItemIds.set(new Set());
    this.resubmitCreditAmountByItemId.set({});
    this.resubmitCreditValidationByItemId.set({});
    this.showResubmitDialog.set(false);
    this.resubmitReasonCode.set('');
    this.resubmitReasonTouched.set(false);
    this.resubmitNotes.set('');
    this.resubmitInlineError.set('');
  }

  isRejectedRma(status: string | null | undefined): boolean {
    return (status || '').replace(/[^a-zA-Z]/g, '').toLowerCase() === 'rejected';
  }

  private isRegularRma(rmaType: string | null | undefined): boolean {
    return (rmaType || '').replace(/[^a-zA-Z]/g, '').toLowerCase() === 'regular';
  }

  private getResubmissionCount(detail: RmaDetailResponse): number {
    const source = detail as unknown as { [key: string]: unknown };
    const value = detail.resubmission_count ?? source['resubmissionCount'];
    return typeof value === 'number' ? value : Number(value || 0);
  }

  private getInitialResubmitCreditAmount(item: RmaDetailItemResponse): string {
    const amount = typeof item.credit_amount === 'number' ? item.credit_amount : this.getItemUnitPrice(item);
    return typeof amount === 'number' && Number.isFinite(amount) ? amount.toFixed(2) : '';
  }

  private updateResubmitCreditValidation(itemId: number, value: string) {
    const detail = this.rmaDetail();
    const item = detail?.rma_items.find((current) => this.getRmaItemId(current) === itemId);
    const unitPrice = item ? this.getItemUnitPrice(item) : null;
    const creditAmount = Number(value);

    this.resubmitCreditValidationByItemId.update((current) => {
      const next = { ...current };
      if (Number.isFinite(creditAmount) && unitPrice !== null && creditAmount > unitPrice) {
        next[itemId] = `Cannot exceed unit price (${this.formatAmount(unitPrice)}).`;
      } else {
        delete next[itemId];
      }
      return next;
    });
  }

  private validateResubmitCreditAmounts(detail: RmaDetailResponse): boolean {
    if (!this.isCreditOnlyRma(detail.rma_type)) {
      return true;
    }

    const removedItemIds = this.removedResubmitItemIds();
    for (const item of detail.rma_items) {
      const itemId = this.getRmaItemId(item);
      if (!removedItemIds.has(itemId)) {
        this.updateResubmitCreditValidation(itemId, this.getResubmitCreditAmount(itemId));
      }
    }

    return Object.keys(this.resubmitCreditValidationByItemId()).length === 0;
  }

  private buildResubmitItems(detail: RmaDetailResponse): RmaResubmitItemRequest[] {
    const isCreditOnly = this.isCreditOnlyRma(detail.rma_type);
    const isRegular = this.isRegularRma(detail.rma_type);
    if (!isRegular && !isCreditOnly) {
      return [];
    }

    const removedIds = this.removedResubmitItemIds();
    const creditAmounts = this.resubmitCreditAmountByItemId();

    return detail.rma_items.map((item) => {
      const rmaItemId = this.getRmaItemId(item);

      if (removedIds.has(rmaItemId)) {
        return {
          item_id: rmaItemId,
          remove: true,
        };
      }

      if (isCreditOnly) {
        const creditAmount = Number(creditAmounts[rmaItemId]);
        return Number.isFinite(creditAmount)
          ? {
              item_id: rmaItemId,
              credit_amount: Number(creditAmount.toFixed(2)),
            }
          : {
              item_id: rmaItemId,
            };
      }

      return {
        item_id: rmaItemId,
      };
    });
  }

  private getRmaItemId(item: RmaDetailItemResponse): number {
    const source = item as unknown as Record<string, unknown>;
    const value = item.item_id ?? item.itemId ?? item.rma_item_id ?? item.rmaItemId ?? source['id'];
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  }

  private async loadResubmitReasonOptions() {
    if (this.resubmitReasonOptions().length > 0) {
      return;
    }

    try {
      const mappings = await this.rmaService.getRmaResubmitReasons();
      const options = mappings
        .map((mapping) => this.mapResubmitReasonOption(mapping))
        .filter((option): option is ResubmitReasonOption => !!option);

      this.resubmitReasonOptions.set(options.length ? options : this.fallbackResubmitReasonOptions);
    } catch {
      this.resubmitReasonOptions.set(this.fallbackResubmitReasonOptions);
    }
  }

  private mapResubmitReasonOption(mapping: ConfigMappingResponse): ResubmitReasonOption | null {
    const source = mapping as Record<string, unknown>;
    const rawValue = mapping.map_value ?? source['mapping_value'] ?? source['value'];
    const value = String(rawValue ?? '').trim();

    return value ? { value, label: value } : null;
  }

  private toReasonCode(value: string): string {
    return value
      .trim()
      .replace(/([a-z])([A-Z])/g, '$1_$2')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toUpperCase();
  }

  private getSelectedResubmitReasonLabel(): string {
    const selected = this.resubmitReasonOptions().find(
      (option) => option.value === this.resubmitReasonCode(),
    );
    return selected?.label || this.resubmitReasonCode();
  }

  private handleResubmitError(error: unknown) {
    if (error instanceof HttpErrorResponse) {
      const message = this.getErrorMessageText(error).toLowerCase();

      if (error.status === 400) {
        if (message.includes('reason')) {
          this.resubmitInlineError.set(this.getErrorMessageText(error) || 'Invalid reason code.');
          return;
        }

        this.resubmitInlineError.set(
          message.includes('already') || message.includes('resubmitted')
            ? 'This RMA has already been resubmitted.'
            : 'This RMA is no longer in a rejected state.',
        );
        return;
      }

      if (error.status === 403) {
        this.resubmitInlineError.set('You are not authorized to resubmit this RMA.');
        return;
      }

      if (error.status === 401) {
        this.resubmitInlineError.set('Your session has expired. Please sign in again.');
        return;
      }

      if (error.status === 422) {
        const validationMessage = this.getValidationErrorMessage(error);
        if (validationMessage.toLowerCase().includes('reason')) {
          this.resubmitReasonTouched.set(true);
        }
        this.resubmitInlineError.set(validationMessage || 'Validation failed. Please review the resubmit details.');
        return;
      }
    }

    this.notifications.showAlert({
      variant: 'warn',
      symbolName: 'report',
      message: 'Failed to resubmit RMA. Please try again.',
    });
  }

  private getErrorMessageText(error: HttpErrorResponse): string {
    if (typeof error.error === 'string') {
      return error.error;
    }

    if (error.error && typeof error.error === 'object') {
      const body = error.error as Record<string, unknown>;
      const message = body['message'] ?? body['error'] ?? body['title'];
      return message === null || message === undefined ? '' : String(message);
    }

    return error.message || '';
  }

  private getValidationErrorMessage(error: HttpErrorResponse): string {
    const body = error.error;
    if (body && typeof body === 'object') {
      const errors = (body as Record<string, unknown>)['errors'];
      if (Array.isArray(errors)) {
        const firstError = errors.find((item) => !!item && typeof item === 'object') as Record<string, unknown> | undefined;
        const message = firstError?.['error_message'] ?? firstError?.['message'];
        if (message !== null && message !== undefined) {
          return String(message);
        }
      }
    }

    return this.getErrorMessageText(error);
  }

  onRejectionReasonChange(value: string) {
    this.rejectionReason.set((value || '').slice(0, this.rejectionReasonMaxLength));
    this.rejectionReasonTouched.set(true);
  }

  openDecisionDialog(decision: 'approved' | 'rejected') {
    if (this.submittingDecision()) {
      return;
    }

    this.decisionComments.set('');
    this.rejectionReason.set('');
    this.rejectionReasonTouched.set(false);
    this.activeDecisionDialog.set(decision);
  }

  closeDecisionDialog() {
    if (this.submittingDecision()) {
      return;
    }

    this.activeDecisionDialog.set(null);
    this.decisionComments.set('');
    this.rejectionReason.set('');
    this.rejectionReasonTouched.set(false);
  }

  getRejectionReasonError(): string {
    return this.rejectionReasonTouched() && !this.rejectionReason().trim()
      ? 'Rejection reason is required.'
      : '';
  }

  async submitDecision(decision: 'approved' | 'rejected') {
    const detail = this.rmaDetail();
    if (!detail || this.submittingDecision() || !this.shouldShowApprovalActions(detail)) {
      return;
    }

    if (decision === 'rejected' && !this.rejectionReason().trim()) {
      this.rejectionReasonTouched.set(true);
      return;
    }

    const payload: RmaDecisionRequest = {
      decision: decision === 'approved' ? 'Approved' : 'Rejected',
      rma_type: detail.rma_type || '',
      comments: this.decisionComments().trim(),
      items: detail.rma_items.map((item) => ({
        item_id: this.getRmaItemId(item),
        compliance_status: this.getItemComplianceStatus(this.getRmaItemId(item)),
      })),
    };

    if (decision === 'rejected') {
      payload.rejection_reason = this.rejectionReason().trim();
    }

    this.submittingDecision.set(true);

    try {
      await this.rmaService.submitRmaDecision(detail.rma_id, payload);
      this.notifications.showAlert({
        variant: 'success',
        symbolName: 'check_circle',
        message: `RMA ${decision === 'approved' ? 'approved' : 'rejected'} successfully.`,
      });
      this.activeDecisionDialog.set(null);
      this.decisionComments.set('');
      this.rejectionReason.set('');
      this.rejectionReasonTouched.set(false);
      await this.loadRmaDetailAndActivity(detail.rma_id);
    } catch (error) {
      const isUnauthorized = error instanceof HttpErrorResponse && [401, 403].includes(error.status);
      if (isUnauthorized) {
        this.activeDecisionDialog.set(null);
      }
      this.notifications.showAlert({
        variant: 'warn',
        symbolName: 'report',
        message: error instanceof HttpErrorResponse && error.status === 401
          ? 'Your session has expired. Please sign in again.'
          : isUnauthorized
            ? 'You are not authorized to perform this action on the RMA.'
          : `Failed to ${decision === 'approved' ? 'approve' : 'reject'} RMA. Please try again.`,
      });
    } finally {
      this.submittingDecision.set(false);
    }
  }

  getTimelineDotClass(variant: string): string {
    return `timeline-dot timeline-dot-${variant}`;
  }

  private resolveTimelineVariant(status: string | undefined): string {
    const normalized = (status || '').trim().toLowerCase();
    if (normalized.includes('create')) return 'created';
    if (normalized.includes('approved')) return 'approved';
    if (normalized.includes('reject')) return 'rejected';
    if (normalized.includes('under review')) return 'under-review';
    if (normalized.includes('review') || normalized.includes('pending')) return 'pending';
    return 'neutral';
  }

  private formatDateTime(value: string | undefined): string {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return parsed.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  formatAmount(amount: number | null | undefined): string {
    if (typeof amount !== 'number' || !Number.isFinite(amount)) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(amount);
  }

  isCreditOnlyRma(rmaType: string | null | undefined): boolean {
    return (rmaType || '').replace(/[^a-zA-Z]/g, '').toLowerCase() === 'creditonly';
  }

  getOrderNumber(detail: RmaDetailResponse): string {
    const source = detail as unknown as { [key: string]: unknown };
    const order = detail.order as { [key: string]: unknown } | undefined;
    const webOrderId =
      order?.['web_order_id'] ??
      source['order_number'] ??
      source['web_order_id'];
    const shipmentId =
      order?.['shipment_id'] ??
      order?.['shipment_number'] ??
      order?.['shipment_Id'] ??
      source['shipment_number'] ??
      source['shipment_id'] ??
      source['shipment_Id'];

    const normalizedWebOrderId =
      webOrderId === null || webOrderId === undefined ? '' : String(webOrderId).trim();
    const normalizedShipmentId =
      shipmentId === null || shipmentId === undefined ? '' : String(shipmentId).trim();

    if (normalizedWebOrderId && normalizedShipmentId) {
      return `${normalizedWebOrderId} - ${normalizedShipmentId}`;
    }

    if (normalizedWebOrderId) {
      return normalizedWebOrderId;
    }

    if (normalizedShipmentId) {
      return normalizedShipmentId;
    }

    return '-';
  }

  getDccCode(detail: RmaDetailResponse): string {
    const source = detail as unknown as { [key: string]: unknown };
    const order = detail.order as { [key: string]: unknown } | undefined;

    return String(order?.['dcc_code'] ?? source['dcc_code'] ?? '-');
  }

  getWarehouseCode(detail: RmaDetailResponse): string {
    const source = detail as unknown as { [key: string]: unknown };
    const order = detail.order as { [key: string]: unknown } | undefined;

    return String(order?.['warehouse_code'] ?? source['warehouse_code'] ?? '-');
  }

  private isApproverForRmaWarehouse(detail: RmaDetailResponse): boolean {
    const warehouseCode = this.normalizeWarehouseCode(this.getWarehouseCode(detail));
    const channelCode = this.normalizeChannelCode(this.getChannelCode(detail));

    return this.approverWarehouseChannels().some(
      (channel) =>
        this.normalizeWarehouseCode(channel.warehouse_code) === warehouseCode &&
        this.normalizeChannelCode(channel.channel_code) === channelCode,
    );
  }

  private canCreateRmaForWarehouseAndChannel(detail: RmaDetailResponse): boolean {
    const warehouseCode = this.normalizeWarehouseCode(this.getWarehouseCode(detail));
    const channelCode = this.normalizeChannelCode(this.getChannelCode(detail));

    return this.creatorWarehouseChannels().some(
      (channel) =>
        this.normalizeWarehouseCode(channel.warehouse_code) === warehouseCode &&
        this.normalizeChannelCode(channel.channel_code) === channelCode,
    );
  }

  private normalizeWarehouseCode(value: string | null | undefined): string {
    const normalized = (value || '').trim().toUpperCase();
    return normalized === '-' ? '' : normalized;
  }

  private normalizeChannelCode(value: string | null | undefined): string {
    const normalized = (value || '').trim().toUpperCase();
    return normalized === '-' ? '' : normalized;
  }

  getChannelCode(detail: RmaDetailResponse): string {
    const source = detail as unknown as { [key: string]: unknown };
    const order = detail.order as { [key: string]: unknown } | undefined;

    return String(order?.['sales_channel_code'] ?? source['sales_channel_code'] ?? '-');
  }

  getShipmentNumber(detail: RmaDetailResponse): string {
    const source = detail as unknown as { [key: string]: unknown };
    const order = detail.order as { [key: string]: unknown } | undefined;

    return String(order?.['shipment_id'] ?? source['shipment_number'] ?? source['shipment_id'] ?? '-');
  }

  private getItemUnitPrice(item: RmaDetailItemResponse): number | null {
    if (typeof item.unit_price === 'number') {
      return item.unit_price;
    }

    const fallbackSource = item as unknown as { [key: string]: unknown };
    const fallback = fallbackSource['unitPrice'] ?? fallbackSource['price'] ?? fallbackSource['amount'];
    return typeof fallback === 'number' ? fallback : null;
  }

  private getItemDeviceName(item: RmaDetailItemResponse): string {
    const source = item as unknown as { [key: string]: unknown };
    const fallback =
      source['sku_description'] ??
      source['skuDescription'] ??
      source['sku_desc'] ??
      source['skuDesc'] ??
      source['description'] ??
      source['model_name'] ??
      source['modelName'] ??
      source['device_name'] ??
      source['product_name'] ??
      source['model'];
    if (typeof fallback === 'string' && fallback.trim().length > 0) {
      return fallback;
    }

    return '-';
  }

  private getItemCapacity(item: RmaDetailItemResponse): string {
    const source = item as unknown as { [key: string]: unknown };
    const capacityValue = source['capacity'] ?? source['storage'] ?? source['memory'];
    return this.normalizeSpecText(capacityValue);
  }

  private getItemCondition(item: RmaDetailItemResponse): string {
    const source = item as unknown as { [key: string]: unknown };

    if (typeof item.reason_code === 'string' && item.reason_code.trim().length > 0) {
      return this.toLabel(item.reason_code).replace(/_/g, ' ');
    }

    const conditionValue = source['condition'];
    return this.normalizeSpecText(conditionValue);
  }

  private normalizeSpecText(value: unknown): string {
    if (value === null || value === undefined) {
      return '-';
    }

    const text = String(value).trim();
    return text.length > 0 ? text : '-';
  }

  private toLabel(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private async loadRmaDetailAndActivity(rmaId: number) {
    let detail: RmaDetailResponse;

    try {
      detail = await firstValueFrom(this.rmaService.getRmaById(rmaId));
    } catch {
      this.rmaDetail.set(null);
      this.activityItems.set([]);
      this.attachments.set([]);
      this.deviceDetailsDatasource.setFilter({ refreshAt: Date.now() });
      return;
    }

    this.rmaDetail.set(detail);
    this.decisionComments.set('');
    this.rejectionReason.set('');
    this.rejectionReasonTouched.set(false);
    this.activeDecisionDialog.set(null);
    this.complianceStatusByItemId.set(
      Object.fromEntries(
        detail.rma_items.map((item) => [this.getRmaItemId(item), item.compliance_status || 'Pending']),
      ),
    );
    this.deviceDetailsDatasource.setFilter({ refreshAt: Date.now() });
    void this.loadApproverWarehouseCodes();
    void this.loadRmaAttachments(detail.rma_id);

    if (!detail.rma_number) {
      this.activityItems.set([]);
      return;
    }

    try {
      const response = await firstValueFrom(this.rmaService.getRmaActivityLogs(detail.rma_number));
      const activityRows = this.getActivityRows(response);
      if (!activityRows.length) {
        this.activityItems.set([]);
      } else {
        this.activityItems.set(this.mapActivityItems(activityRows));
      }
    } catch {
      this.activityItems.set([]);
    }
  }

  private async loadRmaAttachments(rmaId: number) {
    this.attachmentsLoading.set(true);
    this.clearAttachmentThumbnails();

    try {
      const response = await firstValueFrom(this.rmaService.getRmaAttachments(rmaId));
      const source = response as Record<string, unknown>;
      const attachments = Array.isArray(source?.['Attachments'])
        ? source['Attachments']
        : Array.isArray(source?.['attachments'])
          ? source['attachments']
          : [];

      const rmaAttachments = attachments
        .map((attachment) => {
          if (attachment && typeof attachment === 'object') {
            const item = attachment as Record<string, unknown>;
            const id = String(item['id'] ?? item['attachment_id'] ?? item['attachmentId'] ?? '').trim();
            const name = String(item['file_name'] ?? item['fileName'] ?? item['name'] ?? '').trim();
            return id && name ? { id, name } : null;
          }

          return null;
        })
        .filter((attachment): attachment is ImageAttachment => attachment !== null);

      this.attachments.set(rmaAttachments);
    } catch {
      this.attachments.set([]);
    } finally {
      this.attachmentsLoading.set(false);
    }
  }

  startAttachmentThumbnailLoading(_selectedIndex: number) {
    const rmaId = this.rmaDetail()?.rma_id;
    if (!rmaId || this.attachmentBatchSubscription) {
      return;
    }

    this.attachmentViewerOpen = true;
    this.attachmentImagesLoading.set(!this.initialAttachmentBatchStarted);
    this.initialAttachmentBatchStarted = true;
    this.loadNextAttachmentImageBatch(rmaId);
  }

  stopAttachmentThumbnailLoading() {
    this.attachmentViewerOpen = false;
    this.attachmentBatchSubscription?.unsubscribe();
    this.attachmentBatchSubscription = null;
    this.attachmentImagesLoading.set(false);
  }

  private loadNextAttachmentImageBatch(rmaId: number) {
    if (!this.attachmentViewerOpen) {
      return;
    }

    const batch = this.attachments().filter(
      (attachment) =>
        /\.(gif|jpe?g|png|webp|bmp|svg)$/i.test(attachment.name.split('?')[0]) &&
        !this.attachmentThumbnailUrls()[attachment.id] &&
        !this.attemptedAttachmentImageIds.has(attachment.id),
    ).slice(0, 10);

    if (batch.length === 0) {
      this.attachmentImagesLoading.set(false);
      return;
    }

    this.attachmentBatchSubscription = forkJoin(
      batch.map((attachment) =>
        this.rmaService.downloadRmaAttachment(rmaId, attachment.id).pipe(
          rxMap((response) =>
            response.body
              ? ([attachment.id, URL.createObjectURL(response.body)] as const)
              : null,
          ),
          catchError(() => of(null)),
        ),
      ),
    ).subscribe({
      next: (entries) => {
        for (const attachment of batch) {
          this.attemptedAttachmentImageIds.add(attachment.id);
        }
        this.attachmentThumbnailUrls.update((current) => ({
          ...current,
          ...Object.fromEntries(
            entries.filter((entry): entry is readonly [string, string] => entry !== null),
          ),
        }));
        this.attachmentImagesLoading.set(false);
      },
      complete: () => {
        this.attachmentBatchSubscription = null;
        this.loadNextAttachmentImageBatch(rmaId);
      },
    });
  }

  async downloadAttachment(attachment: ImageAttachment) {
    const rmaId = this.rmaDetail()?.rma_id;
    if (!rmaId) {
      return;
    }

    try {
      const response = await firstValueFrom(
        this.rmaService.downloadRmaAttachment(rmaId, attachment.id),
      );
      const blob = response.body;
      if (!blob) {
        return;
      }

      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      link.href = objectUrl;
      link.download = attachment.name;
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      this.notifications.showAlert({
        variant: 'warn',
        symbolName: 'report',
        message: 'Unable to download the attachment.',
      });
    }
  }

  private clearAttachmentThumbnails() {
    this.stopAttachmentThumbnailLoading();
    this.initialAttachmentBatchStarted = false;
    this.attemptedAttachmentImageIds.clear();
    for (const thumbnailUrl of Object.values(this.attachmentThumbnailUrls())) {
      URL.revokeObjectURL(thumbnailUrl);
    }
    this.attachmentThumbnailUrls.set({});
  }

  getAttachmentFileName(attachment: string): string {
    return attachment.split('/').filter(Boolean).pop() || attachment;
  }

  getAttachmentFileType(attachment: string): string {
    const fileName = this.getAttachmentFileName(attachment);
    const extension = fileName.split('.').pop()?.trim();
    return extension ? extension.toUpperCase() : '-';
  }

  private async loadApproverWarehouseCodes() {
    try {
      const response = await this.rmaService.getMyWarehouseChannels();
      const source = response as unknown as { can_Approve?: unknown; can_approve?: unknown };
      const approverEntries = Array.isArray(source.can_Approve)
        ? source.can_Approve
        : Array.isArray(source.can_approve)
          ? source.can_approve
          : [];

      this.approverWarehouseChannels.set(
        approverEntries as WarehouseChannel[],
      );
        this.creatorWarehouseChannels.set(
          Array.isArray(response.can_create) ? response.can_create : [],
        );
    } catch {
      this.approverWarehouseChannels.set([]);
        this.creatorWarehouseChannels.set([]);
    }
  }

  private mapActivityItems(rows: Record<string, unknown>[]): TimelineItem[] {
    const seenActivities = new Set<string>();

    return rows.reduce<TimelineItem[]>((items, row) => {
        const title = this.pickFirstString(row, ['action']) || '-';
        const when = this.pickFirstString(row, ['timestamp']);
        const activityKey = `${title.trim().toLowerCase()}|${when.trim()}`;

        if (seenActivities.has(activityKey)) {
          return items;
        }

        seenActivities.add(activityKey);
        const subtitle = when ? this.formatDateTime(when) : '-';

        items.push({
          title,
          subtitle,
          variant: this.resolveTimelineVariant(title),
        });

        return items;
      }, []);
  }

  private getActivityRows(response: unknown): Record<string, unknown>[] {
    if (Array.isArray(response)) {
      return response.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object');
    }

    if (response && typeof response === 'object') {
      const source = response as Record<string, unknown>;
      const items = source['items'] ?? source['activity_logs'] ?? source['activityLogs'] ?? source['logs'];
      if (Array.isArray(items)) {
        return items.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object');
      }
    }

    return [];
  }

  private pickFirstString(source: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
    }

    return '';
  }
}
